import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type { AnyExtension } from '@tiptap/core';
import { useSelector } from 'react-redux';
import { Email, type EmailCompositionValue } from '@kula-ai/components';
import { customTemplateSelectProps } from '@/components/shared/customTemplateLabel';
import { isTemplateEdited } from '@/components/shared/email/templateSelection';
import { uploadUserInlineImage } from '@/api/userImageUploads/useUploadUserInlineImage';
import { useGetAllUsers } from '@/hooks/useGetAllUsers';
import { useGetDefaultFrom } from '@/hooks/useGetDefaultFrom';
import { selectUsers } from '@/store/users/selectors';
import {
  toSelectOptions,
  toEmailValue,
  fromEmailValue,
  isSameEmailValue,
  resolveSelectableFrom,
  toEditorAttachments,
  toTemplateOptions,
  attachmentKey,
} from './templateEmailValue';
import type {
  TemplateEmailUser,
  TemplateEmailAttachment,
} from './templateEmailValue';

interface SelectedTemplate {
  id?: string | number;
  subject?: string;
  body?: string;
  name?: string;
  [key: string]: unknown;
}

interface TemplateEmailComposerProps {
  formik?: any;
  // Merge-variable payload, already in the category shape kula's picker expects.
  variables?: Record<string, string[]>;
  // Overrides the sender list — some surfaces restrict who may send.
  defaultAllowedFromUsers?: TemplateEmailUser[];
  // The picked template; its subject and body are applied to the composer.
  selectedTemplate?: SelectedTemplate | null;
  // Supplying these moves the template picker into the email card.
  templates?: SelectedTemplate[];
  onTemplateChange?: (_template: SelectedTemplate | null) => void;
  // Server-side search, so the card's picker reaches beyond the first page.
  loadTemplates?: (_input: string) => Promise<SelectedTemplate[]>;
  // Rows the caller pins to the front and refuses to let the user remove.
  nonRemovableAttachments?: TemplateEmailAttachment[];
  onAttachmentRemove?: (_item: TemplateEmailAttachment) => void;
  enableFontControls?: boolean;
  disabled?: boolean;
  // E-sign puts several composers on one form, so these are parameterised.
  showFrom?: boolean;
  subjectFieldName?: string;
  bodyFieldName?: string;
  hideToolbar?: boolean;
  subjectMaxLength?: number;
  // Overrides individual toolbar features; merged over the defaults.
  toolbarFeatures?: Record<string, boolean>;
  // Repo-local tiptap nodes (e.g. the survey pill).
  extraExtensions?: AnyExtension[];
  // Extra toolbar control; takes the tiptap instance, hence a render function.
  renderToolbarStartSlot?: (_editor: unknown) => ReactNode;
}

const templateKey = (template: SelectedTemplate | null | undefined): string =>
  template?.id === undefined || template?.id === null
    ? ''
    : String(template.id);

/**
 * Email composition for the shared template composer, on kula-ai `Email`.
 *
 * Rendered as a FormBuilder custom field so every consuming surface keeps its
 * own Formik shell, submit handler and template picker unchanged.
 *
 * Replaces `useEmailField`, whose `FormEditor` disables tiptap's `hardBreak`
 * and so drops every `<br>` in a stored template body (ENG-3719).
 */
export const TemplateEmailComposer = ({
  formik,
  variables,
  defaultAllowedFromUsers,
  selectedTemplate,
  templates,
  onTemplateChange,
  loadTemplates,
  nonRemovableAttachments = [],
  onAttachmentRemove,
  enableFontControls = false,
  disabled = false,
  showFrom = true,
  subjectFieldName = 'subject',
  bodyFieldName = 'body',
  hideToolbar = false,
  subjectMaxLength,
  toolbarFeatures: toolbarFeatureOverrides,
  extraExtensions,
  renderToolbarStartSlot,
}: TemplateEmailComposerProps) => {
  const { fromUsers } = useGetAllUsers();
  const allUsers = useSelector(selectUsers);
  const { from: defaultFrom } = useGetDefaultFrom({});
  const { values, setValues, submitCount } = formik ?? {};

  // kula validates on blur unless told otherwise, so a never-focused field
  // showed nothing and the surface had to fall back to a toast.
  const attempted = submitCount ?? 0;

  // A surface-supplied list wins: it exists precisely to restrict senders.
  const senders = useMemo(
    () => (defaultAllowedFromUsers ?? fromUsers ?? []) as TemplateEmailUser[],
    [defaultAllowedFromUsers, fromUsers]
  );

  const fromOptions = useMemo(() => toSelectOptions(senders), [senders]);
  const ccBccOptions = useMemo(
    () => toSelectOptions(allUsers as TemplateEmailUser[]),
    [allUsers]
  );

  const resolveUser = useCallback(
    (email: string) =>
      [...senders, ...((allUsers ?? []) as TemplateEmailUser[])].find(
        (user) => user?.email === email
      ),
    [senders, allUsers]
  );

  // Seed only — binding `value` straight to Formik would spin the form.
  const [value, setValue] = useState(() => ({
    ...toEmailValue({
      ...values,
      subject: values?.[subjectFieldName],
      body: values?.[bodyFieldName],
    }),
    template: templateKey(selectedTemplate) || null,
  }));
  const valueRef = useRef(value);

  // Subject/body land on the names this instance owns, not the defaults.
  const toFormPatch = useCallback(
    (next: EmailCompositionValue) => {
      const { subject, body, ...rest } = fromEmailValue(next, resolveUser);
      return {
        ...rest,
        [subjectFieldName]: subject,
        [bodyFieldName]: body,
      };
    },
    [bodyFieldName, resolveUser, subjectFieldName]
  );

  // Present only when the surface hands the picker over to the email card.
  const showTemplate = !!onTemplateChange;
  const templateOptions = useMemo(
    () => toTemplateOptions(templates ?? []),
    [templates]
  );

  const handleChange = useCallback(
    (next: EmailCompositionValue) => {
      const prev = valueRef.current;
      // Deferred: setting parent state inside kula's lifecycle makes Formik flush mid-render.
      if (showTemplate && next.template !== prev.template) {
        const picked = (templates ?? []).find(
          (row) => String(row?.id) === next.template
        );
        queueMicrotask(() => onTemplateChange?.(picked ?? null));
      }

      if (isSameEmailValue(prev, next)) {
        // Still store the row selection so the field reflects the click.
        if (next.template !== prev.template) {
          valueRef.current = next;
          setValue(next);
        }
        return;
      }

      valueRef.current = next;
      setValue(next);
      setValues((current: Record<string, unknown>) => ({
        ...current,
        ...toFormPatch(next),
      }));
    },
    [onTemplateChange, setValues, showTemplate, templates, toFormPatch]
  );

  // Adopt a sender only once the list confirms it is selectable.
  const wantedFrom = resolveSelectableFrom(values?.from, senders, defaultFrom);
  const seededFrom = useRef(false);
  useEffect(() => {
    if (seededFrom.current || !wantedFrom) return;
    if (valueRef.current.from === wantedFrom) return;
    seededFrom.current = true;

    const seeded = { ...valueRef.current, from: wantedFrom };
    valueRef.current = seeded;
    setValue(seeded);
    setValues((current: Record<string, unknown>) => ({
      ...current,
      ...toFormPatch(seeded),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantedFrom]);

  // Keyed so a re-render never re-applies the template over the user's edits.
  const appliedTemplate = useRef(templateKey(selectedTemplate));
  useEffect(() => {
    const key = templateKey(selectedTemplate);
    if (key === appliedTemplate.current) return;
    appliedTemplate.current = key;
    if (!selectedTemplate) return;

    const applied = {
      ...valueRef.current,
      template: key || null,
      subject: selectedTemplate.subject ?? '',
      content: selectedTemplate.body ?? '',
    };
    valueRef.current = applied;
    setValue(applied);
    setValues((current: Record<string, unknown>) => ({
      ...current,
      [subjectFieldName]: applied.subject,
      [bodyFieldName]: applied.content,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplate]);

  const attachments: TemplateEmailAttachment[] = useMemo(
    () => values?.attachments ?? [],
    [values?.attachments]
  );
  const editorAttachments = useMemo(
    () => toEditorAttachments(attachments, nonRemovableAttachments.length),
    [attachments, nonRemovableAttachments.length]
  );

  const handleAttachFiles = useCallback(
    (files: File[]) =>
      setValues((current: Record<string, unknown>) => ({
        ...current,
        attachments: [...attachments, ...files],
      })),
    [attachments, setValues]
  );

  const handleRemoveAttachment = useCallback(
    (key: string) => {
      const index = attachments.findIndex(
        (item, i) => attachmentKey(item, i) === key
      );
      // Pinned rows keep their chip; refuse the removal rather than dropping it.
      if (index < 0 || index < nonRemovableAttachments.length) return;

      onAttachmentRemove?.(attachments[index]);
      setValues((current: Record<string, unknown>) => ({
        ...current,
        attachments: attachments.filter((_item, i) => i !== index),
      }));
    },
    [attachments, nonRemovableAttachments.length, onAttachmentRemove, setValues]
  );

  const toolbarFeatures = useMemo(
    () => ({
      image: true,
      attachment: true,
      fontSize: enableFontControls,
      fontFamily: enableFontControls,
      ...toolbarFeatureOverrides,
    }),
    [enableFontControls, toolbarFeatureOverrides]
  );

  const editorVariables = useMemo(() => ({ vars: variables }), [variables]);

  // kula hands over the tiptap instance once the editor mounts.
  const [editorInstance, setEditorInstance] = useState<unknown>(null);

  // Memoised: a fresh object each render rebuilds the subject input.
  const subjectInputProps = useMemo(
    () => (subjectMaxLength ? { maxLength: subjectMaxLength } : undefined),
    [subjectMaxLength]
  );

  // Flag derived apart from the props object so a keystroke doesn't hand the
  // template `Select` a new identity.
  const templateEdited = useMemo(() => {
    const selected = templateOptions?.find(
      (option) => option.value === value.template
    );
    return isTemplateEdited(value, selected);
  }, [templateOptions, value]);

  const mergedTemplateSelectProps = useMemo(
    () => ({
      ...(loadTemplates
        ? {
            variant: 'async' as const,
            defaultOptions: true,
            loadOptions: async (input: string) =>
              toTemplateOptions(await loadTemplates(input)),
          }
        : {}),
      ...customTemplateSelectProps(templateEdited),
    }),
    [loadTemplates, templateEdited]
  );

  return (
    <Email
      showFrom={showFrom}
      showTo={false}
      showSubject
      showEditor
      enableCcBcc
      showTemplate={showTemplate}
      // The `selectedTemplate` effect applies subject AND body together;
      // letting kula set the body too would apply it twice.
      applyTemplateBodyToEditor={false}
      templateOptions={templateOptions}
      templateSearchable
      templateSearchPlaceholder="Search templates"
      // Keeps server-side search, so the picker still reaches templates beyond
      // the page the surface preloaded.
      templateSelectProps={mergedTemplateSelectProps}
      disabled={disabled}
      submitAttempt={attempted}
      validateOnMount={attempted > 0}
      value={value}
      onChange={handleChange}
      fromOptions={fromOptions}
      ccBccOptions={ccBccOptions}
      placeholders={{ from: 'Select from', subject: 'Enter subject' }}
      subjectVariableInputProps={subjectInputProps}
      editorProps={{
        placeholder: 'Start typing...',
        // Pasted/inserted images POST to `/user_image_uploads` and the editor keeps the
        // returned URL; without it the body stores a base64 data URL.
        uploadInlineImage: uploadUserInlineImage,
        hideToolbar,
        extraExtensions,
        toolbarStartSlot: renderToolbarStartSlot?.(editorInstance),
        onEditorReady: setEditorInstance,
        toolbarFeatures,
        variable: editorVariables,
        attachments: editorAttachments,
        onAttachFiles: handleAttachFiles,
        onRemoveAttachment: handleRemoveAttachment,
      }}
    />
  );
};
